import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ImagePlus, Upload, ArrowLeft, Save } from 'lucide-react';
import { useListing, useUpdateListing } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import toast from 'react-hot-toast';

const CONDITIONS = ['Mint', 'Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played'];

export default function EditListingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);
  const { data: listing, isLoading } = useListing(id!);
  const { mutateAsync: updateListing, isLoading: saving } = useUpdateListing(id!);

  const [title, setTitle]               = useState('');
  const [condition, setCondition]       = useState('');
  const [price, setPrice]               = useState('');
  const [shippingPrice, setShippingPrice] = useState('');
  const [notes, setNotes]               = useState('');
  const [tradeFor, setTradeFor]         = useState('');

  // Existing photos from DB (URLs already uploaded)
  const [existingUrls, setExistingUrls] = useState<string[]>([]);
  // New files queued for upload
  const [newFiles, setNewFiles]         = useState<File[]>([]);
  const [newPreviews, setNewPreviews]   = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill form once listing loads
  useEffect(() => {
    if (!listing) return;
    setTitle(listing.title ?? '');
    setCondition(listing.condition ?? 'Near Mint');
    setPrice(listing.price?.toString() ?? '');
    setShippingPrice(listing.shipping_price?.toString() ?? '');
    setNotes((listing as any).notes ?? '');
    setTradeFor(listing.trade_for ?? '');
    setExistingUrls(listing.image_urls ?? []);
  }, [listing]);

  // Redirect if not owner
  useEffect(() => {
    if (!isLoading && listing && currentUser && currentUser.id !== listing.user_id) {
      navigate(`/listings/${id}`);
    }
  }, [listing, currentUser, isLoading, id, navigate]);

  const totalPhotos = existingUrls.length + newFiles.length;

  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const remaining = 5 - totalPhotos;
    const accepted = files.slice(0, remaining);
    setNewFiles((prev) => [...prev, ...accepted]);
    accepted.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setNewPreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeExisting = (index: number) => {
    setExistingUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNew = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadNewPhotos = async (): Promise<string[]> => {
    if (!newFiles.length) return [];
    const formData = new FormData();
    newFiles.forEach((f) => formData.append('images', f));
    try {
      const apiVersion = import.meta.env.VITE_API_VERSION || 'v1';
      const res = await fetch(`/api/${apiVersion}/uploads/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        body: formData,
      });
      const data = await res.json();
      return data.data?.urls ?? data.urls ?? [];
    } catch {
      return [];
    }
  };

  const handleSave = async () => {
    try {
      const uploadedUrls = await uploadNewPhotos();
      const finalUrls = [...existingUrls, ...uploadedUrls];

      await updateListing({
        title: title || undefined,
        condition: condition as any,
        price: price ? Number(price) : undefined,
        shipping_price: shippingPrice ? Number(shippingPrice) : undefined,
        notes: notes || undefined,
        trade_for: tradeFor || undefined,
        image_urls: finalUrls,
      } as any);

      toast.success('Listing updated');
      navigate(`/listings/${id}`);
    } catch {
      toast.error('Failed to save changes');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!listing) return null;

  const isTrade = listing.listing_type === 'trade';
  const isAuction = listing.listing_type === 'auction';

  return (
    <div className="bg-gray-50 dark:bg-zinc-950 min-h-screen">
      <div className="bg-gradient-to-r from-brand-600 to-accent-500 text-white py-8">
        <div className="container-custom">
          <button
            onClick={() => navigate(`/listings/${id}`)}
            className="inline-flex items-center gap-2 text-brand-100 hover:text-white mb-3 text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to listing
          </button>
          <h1 className="text-2xl font-display font-bold">Edit Listing</h1>
          <p className="text-brand-100 text-sm mt-1">{listing.card?.name ?? listing.item_name}</p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 space-y-6">

          {/* Photos */}
          <div>
            <p className="text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-1">Photos</p>
            <p className="text-xs text-gray-400 dark:text-zinc-500 mb-3">Up to 5 photos. Remove any and add new ones.</p>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAddPhotos}
            />

            <div className="flex flex-wrap gap-3">
              {/* Existing photos */}
              {existingUrls.map((url, i) => (
                <div key={`existing-${i}`} className="relative h-20 w-16 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700 group">
                  <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExisting(i)}
                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {/* New photo previews */}
              {newPreviews.map((src, i) => (
                <div key={`new-${i}`} className="relative h-20 w-16 rounded-lg overflow-hidden border border-brand-300 dark:border-brand-700 group">
                  <img src={src} alt={`New photo ${i + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-brand-600/10" />
                  <button
                    type="button"
                    onClick={() => removeNew(i)}
                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {/* Add more slot */}
              {totalPhotos < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-20 w-16 rounded-lg border-2 border-dashed border-gray-200 dark:border-zinc-700 flex flex-col items-center justify-center gap-1 hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                >
                  <ImagePlus className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
                  <span className="text-xs text-gray-400 dark:text-zinc-500">Add</span>
                </button>
              )}

              {/* Empty state */}
              {totalPhotos === 0 && (
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 dark:border-zinc-700 rounded-xl cursor-pointer hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors">
                  <Upload className="h-5 w-5 text-gray-400 dark:text-zinc-500 mb-1" />
                  <span className="text-sm text-gray-500 dark:text-zinc-500">Click to upload photos</span>
                  <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAddPhotos} />
                </label>
              )}
            </div>

            {newPreviews.length > 0 && (
              <p className="text-xs text-brand-600 dark:text-brand-400 mt-2">{newPreviews.length} new photo{newPreviews.length > 1 ? 's' : ''} queued — will upload on save</p>
            )}
          </div>

          <hr className="border-gray-100 dark:border-zinc-800" />

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Listing Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Charizard Holo 1st Edition PSA 9"
              className="w-full px-4 py-3 border border-gray-200 dark:border-zinc-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100"
            />
          </div>

          {/* Condition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Condition</label>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCondition(c)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    condition === c
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-700'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Price — not shown for trade-only */}
          {!isTrade && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                {isAuction ? 'Starting Price (USD)' : 'Price (USD)'}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500">$</span>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100"
                />
              </div>
            </div>
          )}

          {/* Shipping */}
          {!isTrade && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Shipping Price (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500">$</span>
                <input
                  type="number"
                  value={shippingPrice}
                  onChange={(e) => setShippingPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-zinc-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100"
                />
              </div>
            </div>
          )}

          {/* Trade for */}
          {isTrade && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Looking for</label>
              <textarea
                value={tradeFor}
                onChange={(e) => setTradeFor(e.target.value)}
                rows={3}
                className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any extra details about condition, shipping, etc."
              className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/listings/${id}`)}
              className="flex-1 btn-outline"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
